<template>
    <div v-if="element && element.style && element.style.customCss" v-html="'<style>' + element.style.customCss + '</style>'"></div>
    <div v-if="buttonCss" v-html="'<style>' + buttonCss + '</style>'"></div>
    <div class="sk-element-wrapper" :class="element.id + '-wrapper'" v-if="element.props" :style="styleWrapper" @dblclick.stop="onClick()">
        <template v-if="element.props.text && element.props.text !== '' && element.props.text !== 'null'">
            <a type="button" class="btn twpb-btn" :class="[element.props.class, element.props.themeStyle, element.id]" :href="element.link">
                <i v-if="element.props.icon" class="twpb-btn-icon" :class="element.props.icon"></i>
                {{ element.props.text }}
            </a>
        </template>
        <template v-else>
            <div class="sk-not-selected-element">Add Button Content</div>
        </template>
    </div>
</template>

<script>
export default {
    name: "Button",
    props: {
        element: {
            required: false,
            type: Object,
        },
    },
    methods: {
        onClick() {
            this.$emit("onClick", this.element);
        },
    },
    computed: {
        // New single-string border, with fallback to the legacy split keys.
        borderValue() {
            const s = this.element.style;
            if (s.border && String(s.border).trim() !== "") return s.border;
            return s.borderNumber ? `${s.borderNumber}px ${s.borderStyle || "solid"} ${s.borderColor || "currentColor"}` : "";
        },
        styleWrapper() {
            return {
                textAlign: this.element.style.textAlign,
            };
        },
        // All button styling is emitted as a scoped <style> (NOT inline) using a doubled
        // class `.id.id` — same approach as the published output (ButtonController.php) — so
        // :hover / :active actually win over the base rule (an inline base would beat them).
        buttonCss() {
            const s = this.element.style;
            const p = this.element.props;
            const id = this.element.id;
            if (!id || !p) return "";
            const sel = `.${id}.${id}`;

            // Click (:active) effect applies with or without a theme style — the published
            // output (ButtonController::generateButtonCSS) emits it unconditionally too.
            const effects = { "scale-down": "transform:scale(0.96);", "press-down": "transform:translateY(2px);", darken: "filter:brightness(0.85);" };
            let effectCss = "";
            if (effects[s.clickEffect]) {
                // full transition list — a bare transform/filter transition would override
                // the theme's color/background transitions on this rule
                effectCss += `${sel}{transition:color .15s ease-in-out,background-color .15s ease-in-out,border-color .15s ease-in-out,transform .1s ease,filter .1s ease;}`;
                effectCss += `${sel}:active{${effects[s.clickEffect]}}`;
            }

            if (p.themeStyle) return effectCss; // theme styles handle the rest
            const justifyMap = { left: "flex-start", center: "center", right: "flex-end" };

            let css = `${sel}{align-items:center;justify-content:center;`;
            if (s.textAlign === "stretch") {
                css += "display:flex;text-align:center;";
            } else {
                css += "display:inline-flex;";
                if (s.width && s.width !== "0px") css += `width:${s.width};`;
            }
            if (s.height) css += `height:${s.height};`;
            if (s.color) css += `color:${s.color};`;
            if (s.backgroundColor) css += `background-color:${s.backgroundColor};`;
            if (s.padding) css += `padding:${s.padding};`;
            if (s.margin) css += `margin:${s.margin};`;
            if (s.borderRadius) css += `border-radius:${s.borderRadius};`;
            if (this.borderValue) css += `border:${this.borderValue};`;
            if (s.fontSize && s.fontSize !== "0px") css += `font-size:${s.fontSize};`;
            if (s.fontWeight) css += `font-weight:${s.fontWeight};`;
            if (s.fontStyle) css += `font-style:${s.fontStyle};`;
            css += s.textDecoration ? `text-decoration:${s.textDecoration};` : "text-decoration:none;";
            if (s.buttonTextAlign) css += `justify-content:${justifyMap[s.buttonTextAlign] || s.buttonTextAlign};text-align:${s.buttonTextAlign};`;
            css += "}";

            let hover = `${sel}:hover{`;
            if (s.colorHover) hover += `color:${s.colorHover};`;
            if (s.backgroundColorHover) hover += `background-color:${s.backgroundColorHover};`;
            if (s.borderHover && String(s.borderHover).trim() !== "") hover += `border:${s.borderHover};`;
            hover += s.textDecoration ? `text-decoration:${s.textDecoration};` : "text-decoration:none;";
            hover += "}";
            css += hover;

            css += effectCss;

            if (p.icon) {
                let iconRules = "margin-right:0.5em;";
                if (s.iconSize) iconRules += `font-size:${s.iconSize};`;
                if (s.iconColor) iconRules += `color:${s.iconColor};`;
                css += `${sel} .twpb-btn-icon{${iconRules}}`;
                if (s.iconColorHover) css += `${sel}:hover .twpb-btn-icon{color:${s.iconColorHover};}`;
            }
            return css;
        },
    },
    emits: ["onClick"],
};
</script>
